import { useAuth } from "../../contexts/AuthContext";
import { useEarningsVisibility } from "../../contexts/EarningsVisibilityContext";
import { apiService } from "../../services/apiService";
import { useParams } from "react-router-dom";
import { useEffect, useState, useCallback, useRef } from "react";
import jsPDF from "jspdf";
import ExcelJS from "exceljs";
import * as XLSX from "xlsx";
import { useDateFilter } from "../../contexts/DateFilterContext";
import { frameStatisticsTimestamp, statisticsRange, statisticsApiDate, formatStatisticsInput as formatDateTimeInput } from "../../utils/frameStatisticsDate";
import { formatAmount, formatAmountRaw, formatMoney, formatMoneyCode, getExcelMoneyFormat, roundMoney } from "../../utils/currency";
import "../../assets/css/TableStats.css";
import DoubleCheckBadge from "../../Components/DoubleCheckBadge";
import ManualSecondReviewButton from "../../Components/ManualSecondReviewButton";
import { useDashboardResource } from "../../hooks/useDashboardResource";
import { dashboardRequest } from "../../services/dashboardRequest";

const TableStatsIcon = ({ name }) => {
    const common = {
        viewBox: "0 0 24 24",
        fill: "none",
        stroke: "currentColor",
        strokeWidth: "1.8",
        strokeLinecap: "round",
        strokeLinejoin: "round",
        "aria-hidden": true,
        focusable: "false",
    };

    switch (name) {
        case "frames":
            return <svg {...common}><circle cx="12" cy="12" r="8.5" /><circle cx="12" cy="12" r="3" /><path d="M12 3.5v2M20.5 12h-2M12 20.5v-2M3.5 12h2" /></svg>;
        case "paid":
            return <svg {...common}><circle cx="12" cy="12" r="8.5" /><path d="m8.5 12 2.2 2.2 4.8-5" /></svg>;
        case "given":
            return <svg {...common}><path d="M12 21s-7-4.35-7-11a4 4 0 0 1 7-2.65A4 4 0 0 1 19 10c0 6.65-7 11-7 11Z" /></svg>;
        case "earnings":
            return <svg {...common}><path d="M4 7.5h14a2 2 0 0 1 2 2v8H5a2 2 0 0 1-2-2v-10a2 2 0 0 1 2-2h12" /><path d="M15 11h5v4h-5a2 2 0 1 1 0-4Z" /></svg>;
        case "refresh":
            return <svg {...common}><path d="M20 7v5h-5M4 17v-5h5" /><path d="M6.1 8a7 7 0 0 1 11.4-1.7L20 9M4 15l2.5 2.7A7 7 0 0 0 18 16" /></svg>;
        case "export":
            return <svg {...common}><path d="M12 3v12M7 8l5-5 5 5M5 14v5h14v-5" /></svg>;
        case "calendar":
            return <svg {...common}><rect x="3.5" y="5.5" width="17" height="15" rx="3" /><path d="M8 3.5v4M16 3.5v4M3.5 10h17" /></svg>;
        case "eye":
            return <svg {...common}><path d="M2.5 12s3.5-6 9.5-6 9.5 6 9.5 6-3.5 6-9.5 6-9.5-6-9.5-6Z" /><circle cx="12" cy="12" r="2.5" /></svg>;
        case "eye-off":
            return <svg {...common}><path d="m3 3 18 18M10.6 6.1A9.4 9.4 0 0 1 12 6c6 0 9.5 6 9.5 6a16 16 0 0 1-2.1 2.8M6.6 6.7C4 8.3 2.5 12 2.5 12s3.5 6 9.5 6a9 9 0 0 0 3.1-.5M9.9 9.9a3 3 0 0 0 4.2 4.2" /></svg>;
        case "chevron":
            return <svg {...common}><path d="m8 10 4 4 4-4" /></svg>;
        case "trophy":
            return <svg {...common}><path d="M8 4h8v5a4 4 0 0 1-8 0V4ZM12 13v4M8 20h8M6 6H4v2a3 3 0 0 0 3 3M18 6h2v2a3 3 0 0 1-3 3" /></svg>;
        case "clock":
            return <svg {...common}><circle cx="12" cy="12" r="8.5" /><path d="M12 7.5V12l3 2" /></svg>;
        case "sparkle":
            return <svg {...common}><path d="m12 3 1.2 3.3L16.5 7.5l-3.3 1.2L12 12l-1.2-3.3-3.3-1.2 3.3-1.2L12 3ZM18 14l.8 2.2L21 17l-2.2.8L18 20l-.8-2.2L15 17l2.2-.8L18 14Z" /></svg>;
        default:
            return <svg {...common}><circle cx="12" cy="12" r="8.5" /><path d="M12 8v4M12 16h.01" /></svg>;
    }
};

// Mirrors App\Support\FrameQualification::badge() on the backend.
const QUALIFICATION_BADGES = {
    ai_qualified: { label: "AI qualified", className: "bg-green-lt", hint: "The AI review judged this a genuinely played frame." },
    ai_rejected: { label: "AI not qualified", className: "bg-red-lt", hint: "The AI review marked this frame Not qualified." },
    ai_pending: { label: "Awaiting AI", className: "bg-azure-lt", hint: "Saved successfully and excluded from qualified statistics until AI decides." },
    ai_unavailable: { label: "AI retry scheduled", className: "bg-orange-lt", hint: "AI was temporarily unavailable. This frame stays excluded and will retry automatically with backoff." },
    needs_review: { label: "Needs AI review", className: "bg-yellow-lt", hint: "AI found the evidence insufficient; this frame is excluded pending review." },
    cancelled: { label: "Cancelled by Undo", className: "bg-secondary-lt", hint: "The Win/Reset action was undone and this frame is not counted." },
    fallback_qualified: { label: "Legacy result", className: "bg-blue-lt", hint: "Historical label; current qualification always waits for AI." },
    fallback_rejected: { label: "Legacy result", className: "bg-orange-lt", hint: "Historical label; current qualification always waits for AI." },
    manual_qualified: { label: "Overridden: counted", className: "bg-purple-lt", hint: "A club operator chose to count this frame." },
    manual_rejected: { label: "Override: not qualified", className: "bg-purple-lt", hint: "A club operator marked this frame Not qualified." },
};

const QualificationBadge = ({ frame }) => {
    const qualification = frame.qualification;
    const badge = QUALIFICATION_BADGES[qualification?.badge] || QUALIFICATION_BADGES.ai_pending;
    const analysing = qualification?.badge === "ai_pending";
    const confidence = typeof qualification?.confidence === "number"
        ? ` · ${Math.round(qualification.confidence * 100)}%`
        : "";
    const title = [
        qualification?.summary,
        qualification?.reason_codes?.length ? qualification.reason_codes.join(", ") : null,
        qualification?.model ? `Model: ${qualification.model}` : null,
    ].filter(Boolean).join(" — ") || badge.hint;

    return (
        <span className={`badge ${analysing ? "bg-azure-lt" : badge.className}`} title={title}>
            {analysing ? `${badge.label} · analysing` : `${badge.label}${confidence}`}
        </span>
    );
};

const qualificationLabel = (frame) => frame?.qualifies === false ? 'Not qualified' : 'Qualified';
const frameIsQualified = (frame) => frame?.qualifies !== false;
const billingFramesFor = (frames = [], qualificationApplies = true) => (
    qualificationApplies ? frames.filter(frameIsQualified) : frames
);

const formatRangeLabel = (value) => {
    if (!value) return "Not set";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return new Intl.DateTimeFormat("en-GB", {
        day: "numeric",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
    }).format(date);
};

const TableStats = () => {
    const { id } = useParams();
    const { user } = useAuth();
    const userId = user?.id;
    const { showEarnings, toggleEarningsVisibility } = useEarningsVisibility();
    const { from, to, setFrom, setTo } = useDateFilter();
    const [exportOpen, setExportOpen] = useState(false);
    const [retryingFrameIds, setRetryingFrameIds] = useState({});
    const [retryErrors, setRetryErrors] = useState({});
    const [payingFrameIds, setPayingFrameIds] = useState({});
    const [paymentErrors, setPaymentErrors] = useState({});
    const paymentInFlight = useRef(new Set());
    const exportRef = useRef(null);

    const setRangeFor = useCallback((baseDate) => {
        const { start, end } = statisticsRange(baseDate);
        setFrom(formatDateTimeInput(start));
        setTo(formatDateTimeInput(end));
    }, [setFrom, setTo]);

    const setTodayRange = useCallback(() => setRangeFor(new Date()), [setRangeFor]);
    const setYesterdayRange = useCallback(() => {
        const d = new Date();
        d.setDate(d.getDate() - 1);
        setRangeFor(d);
    }, [setRangeFor]);

    const loadSnapshot = useCallback(async ({ signal }) => {
        const [tableData, frameData, playerData, settingData] = await Promise.all([
            dashboardRequest('/club/tables', { method: 'POST', body: { id: userId }, signal }),
            dashboardRequest('/club/table/frames', { method: 'POST', signal, body: {
                id_table: id,
                from: statisticsApiDate(from),
                to: statisticsApiDate(to),
                id_user: userId,
                qualifying_only: false,
            } }),
            dashboardRequest('/club/players', { method: 'POST', body: { id: userId, include_default: true }, signal }),
            dashboardRequest(`/settings/${userId}`, { signal }),
        ]);
        if (!Array.isArray(tableData.tables) || !Array.isArray(frameData.frames)
            || !Array.isArray(playerData.players) || !settingData.settings
            || typeof settingData.settings !== 'object' || Array.isArray(settingData.settings)) {
            throw new Error('The server returned incomplete table activity. Please retry.');
        }
        const foundTable = tableData.tables.find(t => String(t.id) === String(id));
        if (!foundTable) throw new Error('This table is unavailable or you no longer have access to it.');
        const settings = settingData.settings;
        const snPrice = parseFloat(settings.snooker_pricing_per_game);
        const miniPrice = parseFloat(settings.mini_snooker_pricing_per_game);
        return {
            table: foundTable, allFrames: frameData.frames, players: playerData.players,
            snookerPrice: Number.isFinite(snPrice) ? snPrice : 0,
            miniSnookerPrice: Number.isFinite(miniPrice) ? miniPrice : 0,
            currency: settings.default_currency || '',
        };
    }, [id, from, to, userId]);
    const { data: snapshot, loading, refreshing, error: loadError, refresh: getFrames, update } = useDashboardResource({
        key: JSON.stringify([userId, id, from, to]),
        enabled: Boolean(userId && id && from && to),
        load: loadSnapshot,
    });
    const { table = null, allFrames = [], players = [], snookerPrice = 0, miniSnookerPrice = 0, currency = '' } = snapshot || {};
    const setAllFrames = (next) => update((current) => current ? {
        ...current,
        allFrames: typeof next === 'function' ? next(current.allFrames) : next,
    } : current);

    const switchPaidStatus = async (frameId, paid) => {
        if (paymentInFlight.current.has(frameId)) return;
        paymentInFlight.current.add(frameId);
        setPayingFrameIds(current => ({ ...current, [frameId]: true }));
        setPaymentErrors(current => ({ ...current, [frameId]: null }));
        try {
            await dashboardRequest('/club/frame/paid', { method: 'POST', body: { id: frameId } });
            setAllFrames((prevFrames) => prevFrames.map((frame) =>
                frame.id === frameId ? { ...frame, paid: !paid } : frame
            ));
            await getFrames();
        } catch (error) {
            setPaymentErrors(current => ({ ...current, [frameId]: error?.message || 'Could not update payment. Please retry.' }));
        } finally {
            paymentInFlight.current.delete(frameId);
            setPayingFrameIds(current => ({ ...current, [frameId]: false }));
        }
    };

    const retryAiQualification = async (frameId) => {
        setRetryingFrameIds((current) => ({ ...current, [frameId]: true }));
        setRetryErrors((current) => ({ ...current, [frameId]: null }));

        try {
            // This action is intentionally never stored in the offline queue:
            // the button promises an immediate server-side AI retry.
            const data = await apiService.request('/club/frame/qualification/retry', {
                method: 'POST',
                body: { id: frameId },
                skipOfflineQueue: true,
            });

            if (!data?.success || !data?.frame) {
                throw new Error(data?.message || 'Could not start the AI retry');
            }

            setAllFrames((current) => current.map((frame) =>
                frame.id === frameId ? data.frame : frame
            ));
        } catch (error) {
            console.error('Failed to start AI retry:', error);
            setRetryErrors((current) => ({
                ...current,
                [frameId]: error?.message || 'Could not start AI retry',
            }));
        } finally {
            setRetryingFrameIds((current) => ({ ...current, [frameId]: false }));
        }
    };

    useEffect(() => {
        if (!from || !to) {
            setTodayRange();
        }
    }, [from, to, setTodayRange]);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (exportOpen && exportRef.current && !exportRef.current.contains(e.target)) {
                setExportOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [exportOpen]);

    const resolveWinnerName = (winnerId) => {
        if (!winnerId || players.length === 0) return 'Unknown Player';
        const p = players.find(p => p.id === parseInt(winnerId));
        return p?.name || 'Unknown Player';
    };

    const formatDuration = (frame) => {
        const ms = typeof frame.duration_ms === 'number' ? frame.duration_ms : (() => {
            const endMs = new Date(frame.created_at).getTime();
            const startMs = frame.frame_start_ts ? new Date(frame.frame_start_ts).getTime() : null;
            if (!startMs || isNaN(startMs)) return null;
            return Math.max(0, endMs - startMs);
        })();
        if (ms === null) return '-';
        const totalSeconds = Math.floor(ms / 1000);
        const hours = Math.floor(totalSeconds / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = totalSeconds % 60;
        const hh = String(hours).padStart(2, '0');
        const mm = String(minutes).padStart(2, '0');
        const ss = String(seconds).padStart(2, '0');
        return `${hh}:${mm}:${ss}`;
    };

    const csvSafe = (val) => {
        const s = String(val ?? "");
        const needsQuote = s.includes(",") || s.includes('"') || s.includes("\n") || s.includes("\r");
        const escaped = s.replace(/"/g, '""');
        return needsQuote ? `"${escaped}"` : s;
    };

    const downloadCSV = () => {
        const headers = ["Date", "Duration", "Actions", "Qualification", "Paid", "Winner", "Earnings", "Currency", "From", "To"];
        let totalPaid = 0;
        let totalEarnings = 0;
        const effectivePrice = table?.type === 'snooker' ? (table?.pricing_type === 'mini' ? miniSnookerPrice : snookerPrice) : snookerPrice;
        const qualificationAppliesForExport = table?.type === 'snooker';
        const rows = frames.map(f => {
            const countsForEarnings = !qualificationAppliesForExport || frameIsQualified(f);
            const earned = countsForEarnings && f.paid ? effectivePrice : 0;
            if (countsForEarnings && f.paid) totalPaid += 1;
            totalEarnings += earned;
            return [
                csvSafe(new Date(frameStatisticsTimestamp(f)).toLocaleString()),
                csvSafe(formatDuration(f)),
                csvSafe(f.actions_count ?? '-'),
                csvSafe(qualificationLabel(f)),
                csvSafe(f.paid ? 'Yes' : 'No'),
                csvSafe(resolveWinnerName(f.winner)),
                csvSafe(showEarnings ? formatAmountRaw(earned, currency) : '****'),
                csvSafe(currency || ''),
                csvSafe(from || ''),
                csvSafe(to || '')
            ];
        });
        const totalRow = [
            csvSafe('Total'),
            csvSafe(''),
            csvSafe(''),
            csvSafe(''),
            csvSafe(totalPaid),
            csvSafe(''),
            csvSafe(showEarnings ? formatAmountRaw(totalEarnings, currency) : '****'),
            csvSafe(currency || ''),
            csvSafe(from || ''),
            csvSafe(to || '')
        ];
        const csvBody = [headers.join(","), ...rows.map(r => r.join(",")), totalRow.join(",")].join("\r\n");
        const csvContent = "\uFEFF" + csvBody;
        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        const dateStr = new Date().toISOString().split("T")[0];
        a.download = `table_${table?.name || 'snooker'}_stats_${dateStr}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    const downloadPDF = () => {
        const doc = new jsPDF();
        const pageWidth = 210;
        const pageHeight = 297;
        const margin = 10;
        const lineHeight = 7;
        let y = 20;

        doc.setFontSize(16);
        const title = `Table Statistics – ${table?.name || ''}`;
        const tw = doc.getTextWidth(title);
        doc.text(title, (pageWidth - tw) / 2, y);
        y += 10;

        doc.setFontSize(10);
        const rangeText = `Range: ${from || '-'} to ${to || '-'}`;
        doc.text(rangeText, margin, y);
        y += 8;

        const headers = ["Date", "Duration", "Actions", "Qual.", "Paid", "Winner", "Earnings"];
        const colWidths = [44, 24, 18, 28, 16, 42, 18];
        let x = margin;

        doc.setFillColor(0, 128, 0);
        doc.rect(margin, y - lineHeight, pageWidth - 2 * margin, lineHeight, 'F');
        doc.setTextColor(255, 255, 255);
        headers.forEach((h, i) => {
            doc.text(h, x + 2, y - 2);
            x += colWidths[i];
        });
        y += lineHeight;
        doc.setTextColor(0, 0, 0);

        let totalPaid = 0;
        let totalEarnings = 0;
        const effectivePrice = table?.type === 'snooker' ? (table?.pricing_type === 'mini' ? miniSnookerPrice : snookerPrice) : snookerPrice;
        const qualificationAppliesForExport = table?.type === 'snooker';
        frames.forEach(f => {
            if (y > pageHeight - 20) {
                doc.addPage();
                y = 20;
            }
            let rx = margin;
            const cells = [
                new Date(frameStatisticsTimestamp(f)).toLocaleString(),
                formatDuration(f),
                f.actions_count ?? '-',
                qualificationLabel(f),
                f.paid ? 'Yes' : 'No',
                resolveWinnerName(f.winner),
                showEarnings ? ((!qualificationAppliesForExport || frameIsQualified(f)) && f.paid ? (currency ? formatMoneyCode(effectivePrice, currency) : formatAmount(effectivePrice, currency)) : formatAmount(0, currency)) : '****'
            ];
            if ((!qualificationAppliesForExport || frameIsQualified(f)) && f.paid) totalPaid += 1;
            totalEarnings += (!qualificationAppliesForExport || frameIsQualified(f)) && f.paid ? effectivePrice : 0;
            cells.forEach((c, i) => {
                doc.text(String(c), rx + 2, y - 2);
                rx += colWidths[i];
            });
            y += lineHeight;
            doc.line(margin, y - lineHeight, pageWidth - margin, y - lineHeight);
        });

        if (y > pageHeight - 20) {
            doc.addPage();
            y = 20;
        }
        let rx = margin;
        const totalCells = [
            'Total',
            '',
            '',
            '',
            String(totalPaid),
            '',
            showEarnings ? (currency ? formatMoneyCode(totalEarnings, currency) : formatAmount(totalEarnings, currency)) : '****'
        ];
        totalCells.forEach((c, i) => {
            doc.text(String(c), rx + 2, y - 2);
            rx += colWidths[i];
        });
        y += lineHeight;
        doc.line(margin, y - lineHeight, pageWidth - margin, y - lineHeight);

        doc.save(`table_${table?.name || 'snooker'}_stats_${new Date().toISOString().split('T')[0]}.pdf`);
    };

    const downloadXLSX = async () => {
        const wb = new ExcelJS.Workbook();
        wb.creator = 'Scoreboard';
        wb.created = new Date();
        const ws = wb.addWorksheet('Table Stats');

        const headerRow = ['Field', ...frames.map((_, idx) => `Frame ${idx + 1}`), 'Total'];
        ws.addRow(headerRow);

        const totals = { paid: 0, earnings: 0 };
        const effectivePrice = table?.type === 'snooker' ? (table?.pricing_type === 'mini' ? miniSnookerPrice : snookerPrice) : snookerPrice;
        const qualificationAppliesForExport = table?.type === 'snooker';
        const valEarn = (f) => ((!qualificationAppliesForExport || frameIsQualified(f)) && f.paid ? effectivePrice : 0);

        const addRow = (label, values, total) => ws.addRow([label, ...values, total]);

        addRow('Date', frames.map(f => new Date(frameStatisticsTimestamp(f)).toLocaleString()), '');
        addRow('Duration', frames.map(f => formatDuration(f)), '');
        addRow('Actions', frames.map(f => f.actions_count ?? '-'), '');
        addRow('Qualification', frames.map(qualificationLabel), '');
        const paidVals = frames.map(f => (f.paid ? 'Yes' : 'No'));
        totals.paid = frames.filter(f => (!qualificationAppliesForExport || frameIsQualified(f)) && f.paid).length;
        addRow('Paid', paidVals, totals.paid);
        addRow('Winner', frames.map(f => resolveWinnerName(f.winner)), '');
        const earnVals = frames.map(f => (showEarnings ? roundMoney(valEarn(f), currency) : null));
        totals.earnings = frames.reduce((sum, f) => sum + valEarn(f), 0);
        addRow('Earnings', earnVals, showEarnings ? roundMoney(totals.earnings, currency) : null);
        addRow('Currency', frames.map(() => currency || ''), currency || '');
        addRow('From', frames.map(() => from || ''), from || '');
        addRow('To', frames.map(() => to || ''), to || '');

        ws.columns = headerRow.map((_, i) => ({ width: i === 0 ? 16 : 14 }));
        const header = ws.getRow(1);
        header.font = { bold: true };
        header.alignment = { vertical: 'middle', horizontal: 'center' };
        header.eachCell((cell) => {
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF2B7A0B' } };
            cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
            cell.font = { color: { argb: 'FFFFFFFF' }, bold: true };
        });

        ws.eachRow((row, rowNumber) => {
            if (rowNumber === 1) return;
            row.alignment = { vertical: 'middle', horizontal: 'center' };
            row.getCell(1).alignment = { vertical: 'middle', horizontal: 'left' };
            row.eachCell((cell) => {
                cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
            });
        });

        // Data validation: Paid row
        const paidRowIdx = 1 + 5; // Field, Date, Duration, Actions, Qualification, Paid
        for (let c = 2; c <= headerRow.length; c++) {
            const cell = ws.getCell(paidRowIdx, c);
            cell.dataValidation = { type: 'list', allowBlank: false, formulae: ['"Yes,No"'] };
        }

        // Numeric format for earnings
        const earningsRowIdx = 1 + 7; // Field + rows until Earnings
        for (let c = 2; c <= headerRow.length; c++) {
            const cell = ws.getCell(earningsRowIdx, c);
            cell.numFmt = getExcelMoneyFormat(currency);
        }

        // Conditional formatting: highlight earnings > 0 and paid Yes
        const lastCol = headerRow.length;
        try {
            ws.addConditionalFormatting({
                ref: `${colLetter(2)}${earningsRowIdx}:${colLetter(lastCol)}${earningsRowIdx}`,
                rules: [
                    { type: 'greaterThan', formulae: ['0'], style: { fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFC6EFCE' } } } },
                ],
            });
        } catch (e) {
            for (let c = 2; c <= lastCol; c++) {
                const v = ws.getCell(earningsRowIdx, c).value;
                if (typeof v === 'number' && v > 0) ws.getCell(earningsRowIdx, c).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFC6EFCE' } };
            }
        }

        try {
            ws.addConditionalFormatting({
                ref: `${colLetter(2)}${paidRowIdx}:${colLetter(lastCol)}${paidRowIdx}`,
                rules: [
                    { type: 'containsText', operator: 'containsText', text: 'Yes', style: { fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFC6EFCE' } } } },
                    { type: 'containsText', operator: 'containsText', text: 'No', style: { fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFE5A1' } } } },
                ],
            });
        } catch (e) {
            for (let c = 2; c <= lastCol; c++) {
                const v = String(ws.getCell(paidRowIdx, c).value ?? '');
                if (v === 'Yes') ws.getCell(paidRowIdx, c).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFC6EFCE' } };
                if (v === 'No') ws.getCell(paidRowIdx, c).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFE5A1' } };
            }
        }

        const buf = await wb.xlsx.writeBuffer();
        const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `table_${table?.name || 'snooker'}_stats_${new Date().toISOString().split('T')[0]}.xlsx`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    const downloadXLS = () => {
        const header = ['Frame', 'Date', 'Duration', 'Actions', 'Qualification', 'Paid', 'Winner', 'Earnings', 'Currency', 'From', 'To'];
        const data = [header];

        const effectivePrice = table?.type === 'snooker' ? (table?.pricing_type === 'mini' ? miniSnookerPrice : snookerPrice) : snookerPrice;
        const qualificationAppliesForExport = table?.type === 'snooker';
        const valEarn = (f) => ((!qualificationAppliesForExport || frameIsQualified(f)) && f.paid ? effectivePrice : 0);
        let totalPaid = 0;
        let totalEarnings = 0;

        frames.forEach((f, idx) => {
            const earned = valEarn(f);
            if ((!qualificationAppliesForExport || frameIsQualified(f)) && f.paid) totalPaid += 1;
            totalEarnings += earned;
            data.push([
                `Frame ${idx + 1}`,
                new Date(frameStatisticsTimestamp(f)).toLocaleString(),
                formatDuration(f),
                f.actions_count ?? '-',
                qualificationLabel(f),
                f.paid ? 'Yes' : 'No',
                resolveWinnerName(f.winner),
                showEarnings ? roundMoney(earned, currency) : '',
                currency || '',
                from || '',
                to || ''
            ]);
        });

        data.push([
            'Total',
            '',
            '',
            '',
            '',
            totalPaid,
            '',
            showEarnings ? roundMoney(totalEarnings, currency) : '',
            currency || '',
            from || '',
            to || ''
        ]);

        const ws = XLSX.utils.aoa_to_sheet(data);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Table Stats');
        XLSX.writeFile(wb, `table_${table?.name || 'snooker'}_stats_${new Date().toISOString().split('T')[0]}.xls`, { bookType: 'xls' });
    };

    if (loading) {
        return (
            <main className="table-stats-page table-stats-loading" aria-busy="true">
                <div className="table-stats-loading-inner" role="status">
                    <span className="table-stats-loading-mark" aria-hidden="true" />
                    <span>Preparing table activity…</span>
                </div>
            </main>
        );
    }

    if (!table) {
        return (
            <main className="table-stats-page table-stats-error">
                <span className="table-stats-empty-icon"><TableStatsIcon name="frames" /></span>
                <h1>Table data is unavailable</h1>
                <p role="alert">{loadError || 'Refresh to try loading this table again.'}</p>
                <button type="button" className="btn btn-primary" onClick={getFrames}>Try again</button>
            </main>
        );
    }

    const qualificationApplies = table.type === 'snooker';
    // Every saved frame stays visible in history; billing-style summary cards
    // only use qualified snooker frames.
    const frames = allFrames;
    const visibleFrames = allFrames;
    const qualifiedFrames = qualificationApplies
        ? allFrames.filter(frame => frame.qualifies !== false).length
        : allFrames.length;
    const notQualifiedFrames = qualificationApplies ? allFrames.length - qualifiedFrames : 0;
    const billingFrames = billingFramesFor(frames, qualificationApplies);

    const paidFrames = billingFrames.filter(frame => frame.paid).length;
    const givenFrames = billingFrames.length - paidFrames;
    const effectivePrice = table.type === 'snooker'
        ? (table.pricing_type === 'mini' ? miniSnookerPrice : snookerPrice)
        : snookerPrice;
    const totalEarnings = paidFrames * effectivePrice;
    const collectionRate = billingFrames.length ? Math.round((paidFrames / billingFrames.length) * 100) : 0;
    const periodLabel = `${formatRangeLabel(from)} — ${formatRangeLabel(to)}`;

    return (
        <main className="table-stats-page">
            {loadError && <div className="container-xl alert alert-warning" role="alert">Could not refresh table activity. Your last available figures for this period are still shown. {loadError}</div>}
            <div className="page-header d-print-none table-stats-heading">
                <div className="container-xl">
                    <div className="row g-2 align-items-center">
                        <div className="col">
                            <div className="page-pretitle">Statistics</div>
                            <h2 className="page-title">{table.name}</h2>
                            <p className="table-stats-heading-copy">Every saved frame, qualification status, payment status and estimated takings for this table.</p>
                            <div className="table-stats-period-summary" aria-label={`Selected period: ${periodLabel}`}>
                                <TableStatsIcon name="calendar" />
                                <span>Selected period</span>
                                <strong>{periodLabel}</strong>
                            </div>
                        </div>
                        <div className="col-auto ms-auto d-print-none d-flex table-stats-controls">
                            <div className="input-icon">
                                <input
                                    className="form-control"
                                    placeholder="Select a date"
                                    type="datetime-local"
                                    value={from}
                                    onChange={(e) => setFrom(e.target.value)}
                                    style={{ paddingLeft: '0.75rem' }}
                                />
                            </div>
                            <div className="input-icon">
                                <input
                                    className="form-control"
                                    placeholder="Select a date"
                                    type="datetime-local"
                                    value={to}
                                    onChange={(e) => setTo(e.target.value)}
                                    style={{ paddingLeft: '0.75rem' }}
                                />
                            </div>
                            <button className="btn btn-outline-primary" onClick={setTodayRange} type="button">Today</button>
                            <button className="btn btn-outline-primary" onClick={setYesterdayRange} type="button">Yesterday</button>
                            <button onClick={getFrames} disabled={refreshing} className="btn btn-primary btn-icon" type="button" title="Refresh" aria-label={refreshing ? 'Refreshing table statistics' : 'Refresh'}>
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="icon icon-tabler icons-tabler-outline icon-tabler-refresh">
                                    <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                                    <path d="M20 11a8.1 8.1 0 0 0 -15.5 -2m-.5 -4v4h4" />
                                    <path d="M4 13a8.1 8.1 0 0 0 15.5 2m.5 4v-4h-4" />
                                </svg>
                            </button>
                            <div className="position-relative" ref={exportRef}>
                                <button onClick={() => setExportOpen(o => !o)} className="btn btn-primary d-inline-flex align-items-center" type="button" title="Export" aria-haspopup="menu" aria-expanded={exportOpen} aria-label="Export">
                                    Export
                                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="icon icon-tabler icons-tabler-outline icon-tabler-chevron-down" style={{ marginLeft: '0.25rem' }}>
                                        <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                                        <path d="M6 9l6 6l6 -6" />
                                    </svg>
                                </button>
                                {exportOpen && (
                                    <div className="dropdown-menu show" style={{ position: 'absolute', right: 0, top: '100%', minWidth: '12rem' }} role="menu">
                                        <button className="dropdown-item" onClick={() => { setExportOpen(false); downloadPDF(); }} role="menuitem">
                                            Export as PDF
                                        </button>
                                        <button className="dropdown-item" onClick={() => { setExportOpen(false); downloadXLSX(); }} role="menuitem">
                                            Export as Excel (XLSX)
                                        </button>
                                        <button className="dropdown-item" onClick={() => { setExportOpen(false); downloadXLS(); }} role="menuitem">
                                            Export as Excel (XLS)
                                        </button>
                                        <button className="dropdown-item" onClick={() => { setExportOpen(false); downloadCSV(); }} role="menuitem">
                                            Export as CSV
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="page-body table-stats-body">
                <div className="container-xl w-100">
                    <div className="row row-deck row-cards table-stats-metrics">
                        <div className="col-12 col-sm-6 col-lg-3 table-stats-metric-col">
                            <div className="card card-sm table-stats-metric-card is-frames" role="group" aria-label="Recorded Frames summary">
                                <div className="card-body">
                                    <div className="row align-items-center">
                                        <div className="col-auto">
                                            <span className="bg-primary text-white avatar">
                                                <svg
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    width="24"
                                                    height="24"
                                                    viewBox="0 0 24 24"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    strokeWidth="2"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    className="icon icon-tabler icons-tabler-outline icon-tabler-sport-billard"
                                                >
                                                    <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                                                    <path d="M12 10m-2 0a2 2 0 1 0 4 0a2 2 0 1 0 -4 0" />
                                                    <path d="M12 14m-2 0a2 2 0 1 0 4 0a2 2 0 1 0 -4 0" />
                                                    <path d="M12 12m-8 0a8 8 0 1 0 16 0a8 8 0 1 0 -16 0" />
                                                </svg>
                                            </span>
                                        </div>
                                        <div className="col">
                                            <div className="font-weight-medium">Recorded Frames</div>
                                            <div className="text-secondary">{billingFrames.length}</div>
                                            <small className="table-stats-metric-note">
                                                {qualificationApplies
                                                    ? `${frames.length} recorded · ${notQualifiedFrames} not qualified`
                                                    : "During the selected period"}
                                            </small>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="col-12 col-sm-6 col-lg-3 table-stats-metric-col">
                            <div className="card card-sm table-stats-metric-card is-earnings" role="group" aria-label="Total Earnings summary">
                                <div className="card-body">
                                    <div className="row align-items-center">
                                        <div className="col-auto">
                                            <span className="bg-green text-white avatar">
                                                <svg
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    width="24"
                                                    height="24"
                                                    viewBox="0 0 24 24"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    strokeWidth="2"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    className="icon icon-tabler icons-tabler-outline icon-tabler-currency-dollar"
                                                >
                                                    <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                                                    <path d="M16.7 8a3 3 0 0 0 -2.7 -2h-4a3 3 0 0 0 0 6h4a3 3 0 0 1 0 6h-4a3 3 0 0 1 -2.7 -2" />
                                                    <path d="M12 3v3m0 12v3" />
                                                </svg>
                                            </span>
                                        </div>
                                        <div className="col">
                                            <div className="font-weight-medium">
                                                Total Earnings
                                                <button
                                                    onClick={toggleEarningsVisibility}
                                                    className="btn btn-sm btn-link p-0 ms-2 earnings-toggle"
                                                    type="button"
                                                    title={showEarnings ? 'Hide Earnings' : 'Show Earnings'}
                                                    aria-label={showEarnings ? 'Hide total earnings' : 'Show total earnings'}
                                                >
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`icon icon-tabler icons-tabler-outline ${showEarnings ? 'icon-tabler-eye' : 'icon-tabler-eye-off'}`}>
                                                        <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                                                        {showEarnings ? (
                                                            <>
                                                                <path d="M10 12a2 2 0 1 0 4 0a2 2 0 0 0 -4 0" />
                                                                <path d="M21 12c-2.4 4 -5.4 6 -9 6c-3.6 0 -6.6 -2 -9 -6c2.4 -4 5.4 -6 9 -6c3.6 0 6.6 2 9 6" />
                                                            </>
                                                        ) : (
                                                            <>
                                                                <path d="M10.585 10.587a2 2 0 0 0 2.829 2.828" />
                                                                <path d="M16.681 16.673a8.717 8.717 0 0 1 -4.681 1.327c-3.6 0 -6.6 -2 -9 -6c1.272 -2.12 2.612 -3.873 3.88 -5.121m2.112 -1.96c1.185 -.92 2.354 -1.668 3.508 -2.256" />
                                                                <path d="M3 3l18 18" />
                                                            </>
                                                        )}
                                                    </svg>
                                                </button>
                                            </div>
                                            <div className="text-secondary">{showEarnings ? (currency ? formatMoney(totalEarnings, currency) : formatAmount(totalEarnings, currency)) : '••••'}</div>
                                            <small className="table-stats-metric-note">
                                                {qualificationApplies ? 'Qualified paid frames × current rate' : 'Paid frames × current rate'}
                                            </small>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="col-12 col-sm-6 col-lg-3 table-stats-metric-col">
                            <div className="card card-sm table-stats-metric-card is-paid">
                                <div className="card-body">
                                    <div className="row align-items-center">
                                        <div className="col-auto">
                                            <span className="bg-primary text-white avatar">
                                                <svg
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    width="24"
                                                    height="24"
                                                    viewBox="0 0 24 24"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    strokeWidth="2"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    className="icon icon-tabler icons-tabler-outline icon-tabler-circle-check"
                                                >
                                                    <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                                                    <path d="M12 21a9 9 0 1 0 0 -18a9 9 0 0 0 0 18" />
                                                    <path d="M9 12l2 2l4 -4" />
                                                </svg>
                                            </span>
                                        </div>
                                        <div className="col">
                                            <div className="font-weight-medium">Paid Frames</div>
                                            <div className="text-secondary">{paidFrames}</div>
                                            <small className="table-stats-metric-note">
                                                {collectionRate}% collection rate{qualificationApplies ? ' on qualified frames' : ''}
                                            </small>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="col-12 col-sm-6 col-lg-3 table-stats-metric-col">
                            <div className="card card-sm table-stats-metric-card is-given">
                                <div className="card-body">
                                    <div className="row align-items-center">
                                        <div className="col-auto">
                                            <span className="bg-pink text-white avatar">
                                                <svg
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    width="24"
                                                    height="24"
                                                    viewBox="0 0 24 24"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    strokeWidth="2"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    className="icon icon-tabler icons-tabler-outline icon-tabler-heart"
                                                >
                                                    <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                                                    <path d="M19.5 12.571l-7.5 7.429l-7.5 -7.429a5 5 0 1 1 7.5 -6.571a5 5 0 1 1 7.5 6.571" />
                                                </svg>
                                            </span>
                                        </div>
                                        <div className="col">
                                            <div className="font-weight-medium">Given Frames</div>
                                            <div className="text-secondary">{givenFrames}</div>
                                            <small className="table-stats-metric-note">
                                                {qualificationApplies ? 'Qualified frames without a charge' : 'Recorded without a charge'}
                                            </small>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="col-12 col-md-12 table-stats-activity-col">
                            <div className="card table-stats-activity-card">
                                <div className="card-header">
                                    <div>
                                        <span className="table-stats-section-kicker">Recorded activity</span>
                                        <h3 className="card-title">Frame history</h3>
                                    </div>
                                    <div className="d-flex align-items-center" style={{ gap: '0.75rem' }}>
                                        <span className="table-stats-section-count">{visibleFrames.length} {visibleFrames.length === 1 ? 'frame' : 'frames'}</span>
                                    </div>
                                </div>
                                <div className="frames-container overflow-auto">
                                    {visibleFrames.map((frame, i) => (
                                        <div key={frame.id ?? i} className={`frame-card ${qualificationApplies && frame.qualifies === false ? 'frame-card-not-qualified' : ''}`}>
                                            <div className="frame-card-header">
                                                <div className="d-flex align-items-center">
                                                    <span className="avatar avatar-xs bg-primary text-white rounded-circle me-2">
                                                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="icon icon-tabler icons-tabler-outline icon-tabler-trophy">
                                                            <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                                                            <path d="M8 21l8 0" />
                                                            <path d="M12 17l0 4" />
                                                            <path d="M7 4l10 0" />
                                                            <path d="M17 4v8a5 5 0 0 1 -10 0v-8" />
                                                            <path d="M5 9m-2 0a2 2 0 1 0 4 0a2 2 0 1 0 -4 0" />
                                                            <path d="M19 9m-2 0a2 2 0 1 0 4 0a2 2 0 1 0 -4 0" />
                                                        </svg>
                                                    </span>
                                                    <span className="frame-winner-name text-truncate">
                                                        {resolveWinnerName(frame.winner)}
                                                    </span>
                                                </div>
                                                <div className="frame-date text-muted" title={`Saved: ${new Date(frame.created_at).toLocaleString()}`}>
                                                    {new Date(frameStatisticsTimestamp(frame)).toLocaleString()}
                                                </div>
                                            </div>
                                            <div className="frame-card-bottom d-flex justify-content-between align-items-center flex-wrap" style={{ gap: '1rem' }}>
                                                <div className="frame-duration">
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="icon icon-tabler icons-tabler-outline icon-tabler-clock me-1">
                                                        <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                                                        <path d="M3 12a9 9 0 1 0 18 0a9 9 0 0 0 -18 0" />
                                                        <path d="M12 7v5l3 3" />
                                                    </svg>
                                                    {(() => {
                                                        const ms = typeof frame.duration_ms === 'number' ? frame.duration_ms : (() => {
                                                            const endMs = new Date(frame.created_at).getTime();
                                                            const startMs = frame.frame_start_ts ? new Date(frame.frame_start_ts).getTime() : null;
                                                            if (!startMs || isNaN(startMs)) return null;
                                                            return Math.max(0, endMs - startMs);
                                                        })();
                                                        if (ms === null) return '-';
                                                        const totalSeconds = Math.floor(ms / 1000);
                                                        const hours = Math.floor(totalSeconds / 3600);
                                                        const minutes = Math.floor((totalSeconds % 3600) / 60);
                                                        const seconds = totalSeconds % 60;
                                                        const hh = String(hours).padStart(2, '0');
                                                        const mm = String(minutes).padStart(2, '0');
                                                        const ss = String(seconds).padStart(2, '0');
                                                        return `${hh}:${mm}:${ss}`;
                                                    })()}
                                                </div>
                                                <div className="frame-badges d-flex flex-wrap" style={{ gap: '0.5rem' }}>
                                                    <button
                                                        type="button"
                                                        className={`badge cursor-pointer table-stats-payment-toggle ${frame.paid ? 'bg-green-lt' : 'bg-orange-lt'}`}
                                                        onClick={() => switchPaidStatus(frame.id, frame.paid)}
                                                        disabled={Boolean(payingFrameIds[frame.id])}
                                                        aria-busy={Boolean(payingFrameIds[frame.id])}
                                                        title={frame.paid ? 'Change to given' : 'Change to paid'}
                                                    >
                                                        {frame.paid ? 'Paid' : 'Given'}
                                                    </button>
                                                    {paymentErrors[frame.id] && <span className="ai-retry-error" role="alert">{paymentErrors[frame.id]}</span>}
                                                    <span className="badge bg-blue-lt">Actions: {frame.actions_count ?? '-'}</span>
                                                    {qualificationApplies && <QualificationBadge frame={frame} />}
                                                    {qualificationApplies && <DoubleCheckBadge qualification={frame.qualification} />}
                                                    {qualificationApplies && <ManualSecondReviewButton frame={frame}
                                                        onUpdated={(updated) => setAllFrames((current) => current.map((row) => row.id === updated.id ? updated : row))} />}
                                                    {qualificationApplies && frame.qualification?.badge === 'ai_unavailable' && (
                                                        <button
                                                            type="button"
                                                            className={`ai-retry-button ${retryingFrameIds[frame.id] ? 'is-loading' : ''}`}
                                                            onClick={() => retryAiQualification(frame.id)}
                                                            disabled={Boolean(retryingFrameIds[frame.id])}
                                                            aria-busy={Boolean(retryingFrameIds[frame.id])}
                                                            title="Skip the scheduled delay and start AI qualification now"
                                                        >
                                                            <TableStatsIcon name="refresh" />
                                                            <span>{retryingFrameIds[frame.id] ? 'Starting…' : 'Retry AI now'}</span>
                                                        </button>
                                                    )}
                                                    {retryErrors[frame.id] && (
                                                        <span className="ai-retry-error" role="alert">{retryErrors[frame.id]}</span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                    {!visibleFrames.length && (
                                        <div className="table-stats-empty">
                                            <span className="table-stats-empty-icon"><TableStatsIcon name="sparkle" /></span>
                                            <h4>No recorded frames yet</h4>
                                            <p>Activity recorded during this period will appear here.</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </main>
    );
};

export default TableStats;
const colLetter = (n) => {
    let s = '';
    while (n > 0) { const m = (n - 1) % 26; s = String.fromCharCode(65 + m) + s; n = Math.floor((n - 1) / 26); }
    return s;
};
